import { Injectable, NgModule } from '@angular/core';
import { ActivatedRouteSnapshot, RouterModule, Routes } from '@angular/router';
import { take } from 'rxjs/operators';
import {
  AuthGuard,
  DhhDataDashboardGuard,
  DhhRoleOrDhhStatewideSearchAuthGuard,
  InteractiveAccessAuthGuard,
  NonInteractiveAccessAuthGuard,
  NonPortalUserAuthGuard,
  NonVrAuthGuard,
  PortalAuthGuard,
  UberAdminGuard,
  VrAuthGuard,
} from './auth/auth.guard';
import { UpdateConsentFormComponent } from './consent/family-consent/update-consent-form/update-consent-form.component';
import { EquityCreateCapComponent } from './my-tools/equity/components/equity-create-cap/equity-create-cap.component';
import { EquityLearnerDocumentsComponent } from './my-tools/equity/equity-review/components/equity-iep-review/components/equity-learner-documents/equity-learner-documents.component';
import { NavComponent } from './nav/nav.component';
import { EquityCreateCapGuard, EquityLearnerDocumentGuard } from './shared/guards/equity.guard';
import { FamilyGuard } from './shared/guards/family.guard';
import { CaseSummary } from './shared/models/case';
import { CaseService } from './shared/services/case/case.service';

@Injectable({
  providedIn: 'root',
})
class CaseResolver {
  constructor(private caseService: CaseService) {}

  resolve(next: ActivatedRouteSnapshot) {
    const caseId = next.paramMap.get('caseId');
    return new Promise<CaseSummary>((res) => this.caseService.getCaseSummary(caseId).pipe(take(1)).subscribe(res));
  }
}

const routes: Routes = [
  {
    path: 'early-access-referral',
    loadChildren: () =>
      import('./child-find/early-access-referral/early-access-referral.module').then((mod) => mod.EarlyAccessReferralModule),
  },
  {
    path: 'state-complaint',
    loadChildren: () => import('./state-complaint-public/state-complaint-public.module').then((mod) => mod.StateComplaintPublicModule),
  },
  {
    path: 'mediation',
    loadChildren: () => import('./dispute-resolution-public/mediation/mediation.module').then((mod) => mod.MediationModule),
  },
  {
    path: 'due-process',
    loadChildren: () => import('./due-process-public/due-process-public.module').then((mod) => mod.DueProcessPublicModule),
  },
  {
    path: '',
    component: NavComponent,
    children: [
      {
        path: '',
        pathMatch: 'full',
        loadChildren: () => import('./universal-dashboard/universal-dashboard.module').then((mod) => mod.UniversalDashboardModule),
        canActivate: [FamilyGuard],
      },
      {
        path: 'admin',
        loadChildren: () => import('./admin/admin.module').then((mod) => mod.AdminModule),
        canActivate: [NonVrAuthGuard, NonInteractiveAccessAuthGuard, NonPortalUserAuthGuard],
      },
      {
        path: 'child-find',
        loadChildren: () => import('./child-find/child-find.module').then((mod) => mod.ChildFindModule),
        canActivate: [NonPortalUserAuthGuard],
      },
      {
        path: 'cases/:caseId/evaluation',
        loadChildren: () => import('./evaluation/evaluation.module').then((mod) => mod.EvaluationModule),
      },
      {
        path: 'cases/:caseId/reevaluation',
        loadChildren: () => import('./reevaluation/reevaluation.module').then((mod) => mod.ReevaluationModule),
      },
      {
        path: 'cases/:caseId/reevaluation-plus',
        loadChildren: () => import('./reeval-plus/reeval-plus.module').then((mod) => mod.ReevalPlusModule),
      },
      {
        path: 'learner-management',
        loadChildren: () => import('./learner-management/learner-management.module').then((mod) => mod.LearnerManagementModule),
      },
      {
        path: 'cases/:caseId/iep',
        loadChildren: () => import('./iep/iep.module').then((mod) => mod.IepModule),
      },
      {
        path: 'cases/:caseId/ifsp',
        loadChildren: () => import('./ifsp/ifsp.module').then((mod) => mod.IfspModule),
      },
      {
        path: 'cases/:caseId/behavior-discipline',
        loadChildren: () => import('./behavior-discipline/behavior-discipline.module').then((mod) => mod.BehaviorDisciplineModule),
      },
      {
        path: 'cases/:caseId/pwns',
        loadChildren: () => import('./pwn/pwn.module').then((mod) => mod.PwnModule),
        resolve: {
          case: CaseResolver,
        },
      },
      {
        path: 'sif',
        loadChildren: () => import('./sif/sif.module').then((mod) => mod.SifModule),
        canActivate: [NonVrAuthGuard, NonInteractiveAccessAuthGuard, NonPortalUserAuthGuard],
      },
      {
        path: 'tags',
        loadChildren: () => import('./tags/tags.module').then((mod) => mod.TagsModule),
      },
      {
        path: 'sticky-notes',
        loadChildren: () => import('./sticky-notes/sticky-notes.module').then((mod) => mod.StickyNotesModule),
      },
      {
        path: 'users',
        loadChildren: () => import('./users/users.module').then((mod) => mod.UsersModule),
        canActivate: [NonVrAuthGuard, NonInteractiveAccessAuthGuard, NonPortalUserAuthGuard],
      },
      {
        path: 'consent-signature',
        loadChildren: () => import('./consent-signature/consent-signature.module').then((mod) => mod.ConsentSignatureModule),
      },
      {
        path: 'monitoring-progress',
        loadChildren: () => import('./monitoring-progress/monitoring-progress.module').then((mod) => mod.MonitoringProgressModule),
        canActivate: [NonPortalUserAuthGuard],
      },
      {
        path: 'test',
        loadChildren: () => import('./test/test.module').then((mod) => mod.TestModule),
        canActivate: [UberAdminGuard, NonVrAuthGuard, NonInteractiveAccessAuthGuard],
      },
      {
        path: 'medicaid',
        loadChildren: () => import('./medicaid/medicaid.module').then((mod) => mod.MedicaidModule),
      },
      {
        path: 'canned-report',
        loadChildren: () => import('./canned-report/canned-report.module').then((mod) => mod.CannedReportModule),
        canActivate: [NonPortalUserAuthGuard],
      },
      {
        path: 'portal',
        loadChildren: () => import('./portal/portal.module').then((mod) => mod.PortalModule),
        canActivate: [PortalAuthGuard, NonVrAuthGuard, NonInteractiveAccessAuthGuard],
      },
      {
        path: 'my-tools',
        loadChildren: () => import('./my-tools/my-tools.module').then((mod) => mod.MyToolsModule),
        canActivate: [NonVrAuthGuard, NonPortalUserAuthGuard],
      },
      {
        path: 'surveys',
        loadChildren: () => import('./surveys/surveys.module').then((mod) => mod.SurveyModule),
        canActivate: [NonVrAuthGuard, NonInteractiveAccessAuthGuard],
      },
      {
        path: 'ivrs',
        loadChildren: () => import('./ivrs/ivrs.module').then((mod) => mod.IvrsModule),
        canActivate: [VrAuthGuard],
      },
      {
        path: 'dhh',
        loadChildren: () => import('./dhh/dhh.module').then((mod) => mod.DhhModule),
        canActivate: [DhhRoleOrDhhStatewideSearchAuthGuard],
      },
      {
        path: 'data-dashboard',
        loadChildren: () => import('./my-tools/data-dashboard/data-dashboard.routes').then((mod) => mod.dataDashboardRoutes),
        canActivate: [DhhDataDashboardGuard],
      },
      {
        path: 'interactive-access',
        loadChildren: () => import('./interactive-access/interactive-access.module').then((mod) => mod.InteractiveAccessModule),
        canActivate: [InteractiveAccessAuthGuard],
      },
      {
        path: 'family-member-validation-dashboard',
        loadComponent: () =>
          import('./family-member-validation-dashboard/family-member-validation-dashboard.component').then(
            (m) => m.FamilyMemberValidationDashboardComponent
          ),
      },
      {
        path: 'equity/learners/:learnerId/documents',
        component: EquityLearnerDocumentsComponent,
        canActivate: [EquityLearnerDocumentGuard],
      },
      {
        path: 'equity/create-corrective-action-plan/:equityReviewId',
        component: EquityCreateCapComponent,
        canActivate: [EquityCreateCapGuard],
      },
      {
        path: 'consents',
        component: UpdateConsentFormComponent,
      },
    ],
    canActivate: [AuthGuard],
  },
  {
    path: 'auth',
    loadChildren: () => import('./auth/auth.module').then((mod) => mod.AuthModule),
  },
  {
    path: 'signedforms',
    loadChildren: () => import('./signed-forms/signed-forms.module').then((mod) => mod.SignedFormsModule),
  },
  {
    path: 'documents/:learnerId/:documentId',
    loadComponent: () => import('./shared/components/pdf-viewer/pdf-viewer.component').then((m) => m.PdfViewerComponent),
    canActivate: [AuthGuard],
  },
  {
    path: 'documents/generic/docid/:documentId',
    loadComponent: () => import('./shared/components/pdf-viewer/pdf-viewer.component').then((m) => m.PdfViewerComponent),
    canActivate: [AuthGuard],
  },
  {
    path: 'documents/draft',
    loadComponent: () => import('./shared/components/pdf-draft-viewer/pdf-draft-viewer.component').then((m) => m.PdfDraftViewerComponent),
    canActivate: [AuthGuard],
  },
  {
    path: 'logged-out-sso',
    loadComponent: () => import('./shared/components/logged-out-sso/logged-out-sso.component').then((m) => m.LoggedOutSsoComponent),
  },
  {
    path: 'sandbox-unauthorized',
    loadComponent: () =>
      import('./shared/components/sandbox-unauthorized/sandbox-unauthorized-screen.component').then(
        (m) => m.SandboxUnauthorizedScreenComponent
      ),
  },
  {
    path: 'saml-unauthorized',
    loadComponent: () =>
      import('./shared/components/saml-unauthorized-screen/saml-unauthorized-screen.component').then(
        (m) => m.SamlUnauthorizedScreenComponent
      ),
  },
  {
    path: 'unauthorized',
    loadComponent: () =>
      import('./shared/components/unauthorized-screen/unauthorized-screen.component').then((m) => m.UnauthorizedScreenComponent),
  },
  {
    path: 'health-check',
    loadComponent: () => import('./shared/components/health-check/health-check.component').then((m) => m.HealthCheckComponent),
  },
  {
    path: 'survey-tool',
    loadChildren: () => import('./survey-tool/survey-tool.module').then((mod) => mod.SurveyToolModule),
  },
  {
    path: 'interactive-access-lockout',
    loadChildren: () =>
      import('./interactive-access-lockout/interactive-access-lockout.module').then((mod) => mod.InteractiveAccessLockoutModule),
  },
  {
    path: '*',
    loadComponent: () => import('./shared/components/not-found/not-found.component').then((mod) => mod.NotFoundComponent),
  },
  {
    path: '**',
    loadComponent: () => import('./shared/components/not-found/not-found.component').then((mod) => mod.NotFoundComponent),
  },
];

@NgModule({
  imports: [RouterModule.forRoot(routes)],
  exports: [RouterModule],
})
export class AppRoutingModule {}
